/**
 * Menu de setores do link de suporte.
 *
 * Até aqui uma conexão apontava para **um** setor, escolhido numa lista escrita
 * à mão dentro do código da tela. Quem abria o chat de suporte caía sempre na
 * mesma fila: quem queria o comercial era atendido pelo suporte e transferido à
 * mão, se alguém lembrasse. E acrescentar um setor exigia publicar frontend.
 *
 * Agora o setor é cadastro do tenant, e a conexão pode oferecer vários. O
 * cliente escolhe antes de abrir a conversa, e a escolha viaja no evento para
 * o SGA Desk decidir a fila.
 *
 * Só no canal de formulário. Em canal de texto o menu teria de ser interpretado
 * a partir do que a pessoa digita — número, palavra, erro de digitação — e isso
 * é uma máquina de estado por conversa que não existe aqui. O gateway recusa,
 * e esta tela nem aparece.
 */
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

import { Botao, Campo } from "../components/Sga";
import { api } from "../lib/api";

export interface Setor {
  id: string;
  nome: string;
  ativo: boolean;
  ordem: number;
  /** Faixas próprias deste setor. Vazio = herda do link. */
  horarios?: Faixa[];
}

interface OpcaoMenu {
  setor_id: string;
  rotulo: string;
}

interface MenuLido {
  ativo: boolean;
  cabecalho: string | null;
  fallback_setor_id: string | null;
  opcoes: { id: string; setor_id: string; setor: string; rotulo: string }[];
  horarios: Faixa[];
}

interface Faixa {
  dias: number[];
  inicio: string;
  fim: string;
}

const DIAS = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];

const CABECALHO_PADRAO = "Sobre o que você precisa falar?";

/**
 * Setores ativos do tenant, para as listas suspensas.
 *
 * Substitui o array escrito à mão que existia duplicado em duas telas. Enquanto
 * a lista morava no código, acrescentar "Comercial" era publicar frontend — e
 * as duas cópias podiam divergir sem ninguém notar.
 */
export function useSetores() {
  const [setores, setSetores] = useState<Setor[]>([]);
  useEffect(() => {
    let vivo = true;
    api
      .get<{ itens: Setor[] }>("/setores", { ativos: true })
      .then((r) => {
        if (vivo) setSetores(r.itens);
      })
      // Falhar aqui não pode travar o cadastro inteiro da conexão: a lista fica
      // vazia e o campo mostra o valor que já estava gravado.
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);
  return setores;
}

export function MenuDeSetores({
  conexaoId,
  setorDaConexao,
  podeEscrever,
}: {
  conexaoId: string;
  setorDaConexao: string | null;
  podeEscrever: boolean;
}) {
  const [setores, setSetores] = useState<Setor[]>([]);
  const [ativo, setAtivo] = useState(false);
  const [cabecalho, setCabecalho] = useState("");
  const [fallback, setFallback] = useState<string>("");
  const [opcoes, setOpcoes] = useState<OpcaoMenu[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [novoSetor, setNovoSetor] = useState("");
  const [faixas, setFaixas] = useState<Faixa[]>([]);

  /**
   * A quem o horário sendo editado pertence: `""` é o link, senão o id do setor.
   *
   * Um seletor só, e não uma seção por setor: são as mesmas faixas, os mesmos
   * campos e as mesmas regras — repetir o bloco inteiro para cada setor faria a
   * tela crescer sem ensinar nada, e esconderia que existe herança entre eles.
   */
  const [alvoHorario, setAlvoHorario] = useState("");
  /** Faixas por setor, carregadas junto e salvas só quando mudam. */
  const [faixasSetor, setFaixasSetor] = useState<Record<string, Faixa[]>>({});
  const [setoresTocados, setSetoresTocados] = useState<Set<string>>(new Set());

  /** Faixas do alvo escolhido — o link ou um setor. */
  const faixasAtuais = alvoHorario ? (faixasSetor[alvoHorario] ?? []) : faixas;

  const definirFaixas = (novas: Faixa[]) => {
    if (!alvoHorario) {
      setFaixas(novas);
      return;
    }
    setFaixasSetor({ ...faixasSetor, [alvoHorario]: novas });
    // Só o que foi mexido é salvo: sem isto, abrir a tela e salvar reescreveria
    // o horário de todos os setores, inclusive os que ninguém tocou.
    setSetoresTocados(new Set(setoresTocados).add(alvoHorario));
  };

  async function carregar() {
    setCarregando(true);
    try {
      const [lista, menu] = await Promise.all([
        api.get<{ itens: Setor[] }>("/setores"),
        api.get<MenuLido>(`/connections/${conexaoId}/menu`),
      ]);
      setSetores(lista.itens);
      setAtivo(menu.ativo);
      setCabecalho(menu.cabecalho ?? "");
      setFallback(menu.fallback_setor_id ?? "");
      setOpcoes(menu.opcoes.map((o) => ({ setor_id: o.setor_id, rotulo: o.rotulo })));
      setFaixas(menu.horarios ?? []);
      setFaixasSetor(
        Object.fromEntries(lista.itens.map((s) => [s.id, s.horarios ?? []])),
      );
      setSetoresTocados(new Set());
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não consegui ler o menu");
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conexaoId]);

  const disponiveis = setores.filter(
    (s) => s.ativo && !opcoes.some((o) => o.setor_id === s.id),
  );
  const nomeDoSetor = (id: string) => setores.find((s) => s.id === id)?.nome ?? "?";

  function adicionar() {
    const s = disponiveis[0];
    if (!s) return;
    // O rótulo nasce igual ao nome do setor: é o que o cliente lê, e na maioria
    // dos casos "Financeiro" já é a frase certa. Quem quiser "Segunda via de
    // boleto" edita ali mesmo.
    setOpcoes([...opcoes, { setor_id: s.id, rotulo: s.nome }]);
  }

  async function criarSetor() {
    const nome = novoSetor.trim();
    if (!nome) return;
    setErro(null);
    try {
      const s = await api.post<Setor>("/setores", { nome });
      setSetores([...setores, s].sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome)));
      setNovoSetor("");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não consegui criar o setor");
    }
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      await api.put(`/connections/${conexaoId}/menu`, {
        ativo,
        cabecalho: cabecalho.trim() || null,
        fallback_setor_id: fallback || null,
        opcoes: opcoes.map((o) => ({ setor_id: o.setor_id, rotulo: o.rotulo.trim() })),
        horarios: faixas,
      });

      // Horário por setor vai em chamadas próprias: é outro recurso, e um
      // setor pode ser oferecido por vários links.
      for (const setorId of setoresTocados) {
        await api.put(`/setores/${setorId}/horarios`, {
          horarios: faixasSetor[setorId] ?? [],
        });
      }
      setSetoresTocados(new Set());
      setAviso("Menu salvo.");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "não consegui salvar");
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) return <div style={{ color: "var(--sga-fg-3)" }}>Carregando menu…</div>;

  return (
    <div style={{ marginTop: 18 }}>
      <div className="cad-secao">
        <Icon icon="mdi:format-list-bulleted" width={17} style={{ color: "var(--sga-primary-text)" }} />
        <span className="cad-secao__titulo">Menu de setores</span>
        <span className="cad-secao__linha" />
        <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={ativo}
            disabled={!podeEscrever}
            onChange={(e) => setAtivo(e.target.checked)}
          />
          Menu ativo
        </label>
      </div>

      <p style={{ fontSize: 12.5, color: "var(--sga-fg-3)", margin: "0 0 12px" }}>
        Com o menu ligado, quem abre o link escolhe o setor antes de começar a
        conversa. Desligado, todo chamado vai para{" "}
        <strong>{setorDaConexao || "o setor da conexão"}</strong>.
      </p>

      <Campo rotulo="Mensagem de cabeçalho" hint="O que o cliente lê acima das opções.">
        <input
          className="input"
          value={cabecalho}
          disabled={!podeEscrever}
          maxLength={140}
          placeholder={CABECALHO_PADRAO}
          onChange={(e) => setCabecalho(e.target.value)}
        />
      </Campo>

      <div style={{ marginTop: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <span className="field-label" style={{ color: "var(--sga-primary-text)" }}>
            OPÇÕES → SETOR
          </span>
          <span style={{ flex: 1 }} />
          <Botao
            variante="link"
            icone="mdi:plus"
            onClick={adicionar}
            disabled={!podeEscrever || disponiveis.length === 0}
            title={
              disponiveis.length === 0
                ? "Todos os setores ativos já estão no menu"
                : undefined
            }
          >
            Adicionar opção
          </Botao>
        </div>

        {opcoes.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--sga-fg-3)", padding: "8px 0" }}>
            Nenhuma opção. Com o menu ligado e sem opção, o cliente veria uma
            tela de escolha vazia — por isso o gateway recusa salvar assim.
          </div>
        )}

        {opcoes.map((o, i) => (
          <div
            key={`${o.setor_id}-${i}`}
            style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}
          >
            <span style={{ width: 18, color: "var(--sga-fg-3)", fontSize: 13 }}>{i + 1}</span>
            <input
              className="input"
              style={{ flex: 2 }}
              value={o.rotulo}
              disabled={!podeEscrever}
              maxLength={60}
              placeholder="O que o cliente lê"
              onChange={(e) => {
                const c = [...opcoes];
                c[i] = { ...c[i], rotulo: e.target.value };
                setOpcoes(c);
              }}
            />
            <select
              className="select"
              style={{ flex: 1 }}
              value={o.setor_id}
              disabled={!podeEscrever}
              onChange={(e) => {
                const c = [...opcoes];
                c[i] = { ...c[i], setor_id: e.target.value };
                setOpcoes(c);
              }}
            >
              {setores
                .filter((s) => s.ativo || s.id === o.setor_id)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
            </select>
            <Botao
              variante="link"
              icone="mdi:close"
              disabled={!podeEscrever}
              onClick={() => setOpcoes(opcoes.filter((_, j) => j !== i))}
              title="Remover esta opção"
            />
          </div>
        ))}
      </div>

      <div className="line line-2" style={{ marginTop: 14 }}>
        <Campo
          rotulo="Sem escolha → setor"
          hint="Para onde vai quem fechar a página sem escolher."
        >
          <select
            className="select"
            value={fallback}
            disabled={!podeEscrever}
            onChange={(e) => setFallback(e.target.value)}
          >
            <option value="">{setorDaConexao || "Setor da conexão"}</option>
            {setores
              .filter((s) => s.ativo)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
          </select>
        </Campo>

        <Campo rotulo="Novo setor" hint="Cria no cadastro do tenant e já entra nas listas.">
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="input"
              value={novoSetor}
              disabled={!podeEscrever}
              maxLength={60}
              placeholder="Ex.: Comercial"
              onChange={(e) => setNovoSetor(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void criarSetor();
                }
              }}
            />
            <Botao icone="mdi:plus" onClick={criarSetor} disabled={!podeEscrever || !novoSetor.trim()}>
              Criar
            </Botao>
          </div>
        </Campo>
      </div>

      {/* Faixas de horário.
          Uma faixa só não bastava: sábado abre meio período e domingo tem
          plantão curto, e com um intervalo único a escolha era mentir num dos
          dois. E um horário por link também não bastou, desde que o mesmo link
          passou a oferecer suporte e comercial — o comercial fecha ao meio-dia
          e o suporte segue até as 18h. */}
      <div style={{ marginTop: 18 }}>
        <div className="cad-secao">
          <Icon icon="mdi:clock-outline" width={16} style={{ color: "var(--sga-primary-text)" }} />
          <span className="cad-secao__titulo">Horário de atendimento</span>
          <span className="cad-secao__linha" />
          <span className="cad-secao__nota">
            {faixasAtuais.length
              ? `${faixasAtuais.length} faixa(s)`
              : alvoHorario
                ? "herdando do link"
                : "herdando do cadastro da empresa"}
          </span>
        </div>

        {/* A quem este horário se aplica.
            Sem o seletor, a única opção era um horário por link — e quem
            escolhesse "Comercial" às 17h veria que está dentro do horário,
            escreveria, e ninguém responderia até o dia seguinte. */}
        <Campo rotulo="Aplicar a">
          <select
            className="input"
            value={alvoHorario}
            disabled={!podeEscrever}
            onChange={(e) => setAlvoHorario(e.target.value)}
          >
            <option value="">Este link (vale para todos os setores)</option>
            {opcoes.map((o) => {
              const setor = setores.find((s) => s.id === o.setor_id);
              const proprias = (faixasSetor[o.setor_id] ?? []).length;
              return (
                <option key={o.setor_id} value={o.setor_id}>
                  {setor?.nome ?? o.rotulo}
                  {proprias ? ` · ${proprias} faixa(s)` : " · herda do link"}
                </option>
              );
            })}
          </select>
        </Campo>

        <p style={{ fontSize: 12, color: "var(--sga-fg-3)", margin: "6px 0 12px" }}>
          {alvoHorario
            ? "Este setor aparece com o próprio horário na tela do cliente, e avisa quando está fora dele. Sem faixa nenhuma, ele volta a seguir o horário do link."
            : "Vale para os setores que não tiverem horário próprio. Sem faixa nenhuma, segue o cadastro da empresa."}
        </p>

        {faixasAtuais.map((f, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexWrap: "wrap",
              padding: "10px 0",
              borderTop: i ? "1px solid var(--sga-border-gray)" : undefined,
            }}
          >
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1, minWidth: 220 }}>
              {DIAS.map((d, j) => {
                const iso = j + 1;
                const on = f.dias.includes(iso);
                return (
                  <button
                    key={d}
                    type="button"
                    disabled={!podeEscrever}
                    onClick={() => {
                      const c = [...faixasAtuais];
                      c[i] = {
                        ...f,
                        dias: on
                          ? f.dias.filter((x) => x !== iso)
                          : [...f.dias, iso].sort((a, b) => a - b),
                      };
                      definirFaixas(c);
                    }}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      cursor: "pointer",
                      fontSize: 12.5,
                      border: "1px solid var(--sga-border)",
                      background: on ? "var(--sga-primary)" : "transparent",
                      color: on ? "#fff" : "var(--sga-fg-1)",
                    }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <input
              className="input"
              type="time"
              style={{ width: 120 }}
              value={f.inicio}
              disabled={!podeEscrever}
              onChange={(e) => {
                const c = [...faixasAtuais];
                c[i] = { ...f, inicio: e.target.value };
                definirFaixas(c);
              }}
            />
            <span style={{ color: "var(--sga-fg-3)", fontSize: 13 }}>até</span>
            <input
              className="input"
              type="time"
              style={{ width: 120 }}
              value={f.fim}
              disabled={!podeEscrever}
              onChange={(e) => {
                const c = [...faixasAtuais];
                c[i] = { ...f, fim: e.target.value };
                definirFaixas(c);
              }}
            />
            <Botao
              variante="link"
              icone="mdi:close"
              disabled={!podeEscrever}
              onClick={() => definirFaixas(faixasAtuais.filter((_, j) => j !== i))}
              title="Remover esta faixa"
            />
          </div>
        ))}

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
          <Botao
            variante="link"
            icone="mdi:plus"
            disabled={!podeEscrever || faixasAtuais.length >= 7}
            onClick={() =>
              definirFaixas([
                ...faixasAtuais,
                // A primeira nasce como semana comercial; as seguintes, como
                // fim de semana — que e o caso que motivou as faixas.
                faixasAtuais.length === 0
                  ? { dias: [1, 2, 3, 4, 5], inicio: "08:00", fim: "18:00" }
                  : { dias: [6], inicio: "08:00", fim: "12:00" },
              ])
            }
          >
            Adicionar faixa
          </Botao>
          {faixasAtuais.some((f) => f.fim <= f.inicio) && (
            <span style={{ fontSize: 12.5, color: "var(--sga-warning, #e69138)" }}>
              O fim precisa ser depois do início em cada faixa.
            </span>
          )}
        </div>
      </div>

      {/* Prévia: a mesma frase e os mesmos rótulos que o cliente vai ver. Sem
          ela, só dá para conferir o texto abrindo o link num anônimo. */}
      <div
        style={{
          marginTop: 16,
          padding: 14,
          borderRadius: 10,
          border: "1px solid var(--sga-border-gray)",
          background: "var(--sga-bg-page)",
        }}
      >
        <div className="field-label" style={{ marginBottom: 8 }}>
          PRÉVIA DA TELA DO CLIENTE
        </div>
        <div style={{ fontSize: 14, marginBottom: 10 }}>
          {cabecalho.trim() || CABECALHO_PADRAO}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {opcoes.map((o, i) => (
            <span
              key={i}
              style={{
                padding: "8px 15px",
                borderRadius: 999,
                fontSize: 13,
                border: "1px solid var(--sga-border)",
                background: i === 0 ? "var(--sga-primary)" : "transparent",
                color: i === 0 ? "#fff" : "var(--sga-fg-1)",
              }}
              title={`Vai para ${nomeDoSetor(o.setor_id)}`}
            >
              {o.rotulo || "(sem rótulo)"}
            </span>
          ))}
        </div>
      </div>

      {erro && (
        <div style={{ marginTop: 10, color: "var(--sga-error, #ff4d4f)", fontSize: 13 }}>{erro}</div>
      )}
      {aviso && (
        <div style={{ marginTop: 10, color: "var(--sga-primary-text)", fontSize: 13 }}>{aviso}</div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
        <Botao variante="primary" onClick={salvar} disabled={!podeEscrever || salvando}>
          {salvando ? "Salvando…" : "Salvar menu"}
        </Botao>
      </div>
    </div>
  );
}
